/**
 * What a form is CALLED. The ONE rule, for every surface that shows one.
 *
 * It lives in the package because the consumers span the host/remote boundary:
 * the register, the delete dialog and the run draft are in `apps/pms`, while the
 * Forms sidebar panel is in the gateway — and a remote may not import shell
 * source, nor the shell a remote's. So a copy in each was the only other option,
 * and there were four of them; the gateway's had drifted to a different answer.
 */

/**
 * Is this the server's create-time default, or a name an admin chose?
 *
 * A blank draft is named `Form 4` — numbered so a register holding three of them
 * can be told apart, which "Untitled form" could not. A generated name must not
 * masquerade as a deliberate one, so callers substitute the designation instead:
 * that describes the form, and a sequence number does not.
 *
 * `Untitled form` is still matched for drafts created before the numbering, which
 * are real rows in real workspaces and would otherwise start reading as named.
 */
const GENERATED = /^Form \d+$/i;

export function isGeneratedFormName(name: string | null | undefined): boolean {
  const trimmed = name?.trim();
  if (!trimmed) return true;
  return trimmed === 'Untitled form' || GENERATED.test(trimmed);
}

/**
 * The form's own name, else the designation standing in for a generated one.
 *
 * `designationLabel` is passed already resolved: the register resolves it through
 * the workspace masters, the sidebar reads the denormalised `designation_name`,
 * and neither dependency belongs in here.
 */
export function formTitle(
  name: string | null | undefined,
  designationLabel: string | null | undefined,
): string {
  const trimmed = name?.trim();
  if (trimmed && !isGeneratedFormName(trimmed)) return trimmed;

  const label = designationLabel?.trim();
  if (label) return label;

  /**
   * No designation yet — a form created seconds ago, before step 1.
   *
   * The GENERATED name is better than the literal here, and that is the whole
   * reason it is numbered: three blank drafts read as `Form 3` / `Form 4` /
   * `Form 5`, where "Untitled form" three times says nothing. The literal is only
   * for a row carrying neither.
   */
  return trimmed || 'Untitled form';
}
